import { Action } from '../actions/reviews';
import { toQueryString } from '../../lib/utils/querystring';

const defaultState = {
  isFetching: false,
  isFetchingStation: {},
  isFetchingSettings: {},
  isSubmitting: false,
  byId: {},
  byStationId: {},
  pagingByStationId: {},
  ratingCategories: [],
  isDisabledMessage: undefined,
  hasDisabledReviews: false,
  cachedReviewsKeys: [],
  error: undefined,
  reviewSubmitted: false,
  isFlagging: false,
};

export function getReviewsByStationIdFromReducer(stationId, reducerState) {
  if (!reducerState) {
    return undefined;
  }

  const { byStationId, byId } = reducerState;
  const reviewIds = byStationId[stationId.toString()];
  return reviewIds?.length ? reviewIds.reduce((reviews, reviewId) => reviews.concat(byId[reviewId]), []) : undefined;
}

export function buildCachedReviewsKey(stationId, limit) {
  return toQueryString({ sid: stationId, limit });
}


export default function reviewsReducer(state = defaultState, action) {
  const { byId, byStationId } = state;
  const body = action?.payload;

  switch (action?.type) {
    case Action.Reviews.ReviewsPending:
      return {
        ...state,
        isFetching: true,
        error: undefined,
        isFetchingStation: {
          ...state.isFetchingStation,
          [action.meta.stationId]: true,
        },
        pagingByStationId: {
          ...state.pagingByStationId,
          [action.meta.stationId]: undefined,
        },
        cachedReviewsKeys:
          state.cachedReviewsKeys.includes(buildCachedReviewsKey(action.meta.stationId, action.meta.limit))
            ? state.cachedReviewsKeys
            : state.cachedReviewsKeys.concat([buildCachedReviewsKey(action.meta.stationId, action.meta.limit)]),
        isSubmitting: false,
        reviewSubmitted: undefined,
      };

    case Action.Reviews.ReviewsCompleted:
      if (body.reviews) {
        body.reviews.forEach((review) => {
          const stationId = review.station.id;

          byId[review.id] = review;

          if (!byStationId[stationId]) {
            byStationId[stationId] = [];
          }

          if (!byStationId[stationId].includes(review.id)) {
            byStationId[stationId].push(review.id);
          }
        });
      }

      return {
        ...state,
        isFetching: false,
        byStationId: { ...byStationId },
        isFetchingStation: {
          ...state.isFetchingStation,
          [action.meta.stationId]: false,
        },
        pagingByStationId: {
          ...state.pagingByStationId,
          [action.meta.stationId]: body.paging,
        },
      };

    case Action.Reviews.ReviewsFailed:
      return {
        ...state,
        isFetching: false,
        isFetchingStation: {
          ...state.isFetchingStation,
          [action.meta.stationId]: false,
        },
        error: body,
      };

    case Action.Reviews.ReviewSettingsPending:
      return {
        ...state,
        isFetchingSettings: {
          ...state.isFetchingSettings,
          [action.meta.stationId]: true,
        },
        error: undefined,
      };

    case Action.Reviews.ReviewSettingsCompleted:
      return {
        ...state,
        isFetchingSettings: {
          ...state.isFetchingSettings,
          [action.meta.stationId]: false,
        },
        hasDisabledReviews: body ? body.disabled : false,
        isDisabledMessage: body ? body.disabledMessage : '',
        ratingCategories: (body && body.ratingCategories) || [],
        error: (!body || body.error),
      };

    case Action.Reviews.ReviewSettingsFailed:
      return {
        ...state,
        isFetchingSettings: {
          ...state.isFetchingSettings,
          [action.meta.stationId]: false,
        },
        error: body,
      };

    case Action.Reviews.SubmitReviewPending:
      return {
        ...state,
        isFetching: false,
        isSubmitting: true,
        reviewSubmitted: undefined,
        error: undefined,
      };

    case Action.Reviews.SubmitReviewCompleted:
      return {
        ...state,
        isSubmitting: false,
        hasDisabledReviews: false,
        isDisabledMessage: '',
        reviewSubmitted: true,
        error: body?.error?.message,
      };

    case Action.Reviews.SubmitReviewFailed:
      return {
        ...state,
        isSubmitting: false,
        error: body,
      };

    // Since it would be ideal to show the updates within review box itself to indicate that it is updating
    // the same review is getting updated here with additional props, i.e. isUpdating, updateError
    case Action.Reviews.UpdateReviewPending:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.meta.id]: {
            ...state.byId[action.meta.id],
            isUpdating: true,
            updateError: undefined,
          },
        },
      };

    case Action.Reviews.UpdateReviewCompleted:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.meta.id]: {
            ...state.byId[action.meta.id],
            ...['agree_with_review', 'unagree_with_review'].includes(body.appliedAction) && {
              agreeTotal: action.meta.hasAgreed
                ? state.byId[action.meta.id].agreeTotal + 1
                : state.byId[action.meta.id].agreeTotal - 1,
              hasAgreed: action.meta.hasAgreed,
              // Once review is agreed, it cannot be flagged.
              // If an agreed review is unagreed again by user, then it can be flagged.
              isReportable: !action.meta.hasAgreed,
            },
            ...action.meta.hasFlagged && {
              // any review that is flagged once cannot be flagged again
              isReportable: false,
            },
            isUpdating: false,
          },
        },
      };

    case Action.Reviews.UpdateReviewFailed:
      return {
        ...state,
        ...action.meta.hasAgreed !== 'undefined' && {
          byId: {
            ...state.byId,
            [action.meta.id]: {
              ...state.byId[action.meta.id],
              isUpdating: false,
              updateError: body?.error?.message || 'There was an error updating review, please try again later.',
            },
          },
        },
      };

    default:
      return state;
  }
}
